<template>
  <div class="container-fluid mt-3">
    <!-- Excel Export Button -->
    <div class="d-flex justify-content-end mb-3">
      <button 
        type="button" 
        class="btn btn-success" 
        @click="exportToExcel"
        :disabled="!personalExpenses || personalExpenses.length === 0"
      >
        <i class="fas fa-file-excel me-2"></i>
        Excel'e Aktar
      </button>
    </div>

    <div class="row gx-3 gy-3">
      <div v-for="(expense, index) in personalExpenses" :key="index" class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ expense.personalname }}</h5>
            <small class="text-muted">{{ expense.personalrolename }}</small>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <!-- OSGB Satış Tutarı - Tek Satır -->
              <div class="col-12">
                <label class="form-label">OSGB Satış Tutarı</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(totalOsgbSales)"
                    readonly
                />
              </div>

              <!-- Brüt Ücret, SGK Matrahı, SGK Günü -->
              <div class="col-md-4">
                <label class="form-label">Brüt Ücret</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateGrossSalary(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">SGK Matrahı</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateInsuranceBase(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">SGK Günü</label>
                <input
                    type="number"
                    class="form-control"
                    v-model="expense.insuranceday"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">SGK Oranı (%)</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatSgkRate(expense.sgkrate)"
                    readonly
                />
              </div>

              <!-- SGK İşveren Payı, Kıdem Tutarı, Giderler Toplamı -->
              <div class="col-md-4">
                <label class="form-label">SGK İşveren Payı</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateSgkEmployerShare(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Kıdem Tutarı</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateSeniority(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Giderler Toplamı</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateTotalExpenses(expense))"
                    readonly
                />
              </div>

              <!-- Toplam Maliyet, Fatura Tutarı, Yönetim Gideri Öncesi Kar -->
              <div class="col-md-4">
                <label class="form-label">Toplam Maliyet</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateTotalCost(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Fatura Tutarı (Personel)</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(expense.invoiceamount || expense.contractprice)"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Yönetim Gideri Öncesi Kar</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateProfitBeforeManagement(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Yönetim Gideri Öncesi Kar Oranı (%)</label>
                <input
                    type="text"
                    class="form-control"
                    :value="calculateProfitBeforeManagementRate(expense) + '%'"
                    readonly
                />
              </div>

              <!-- Yönetim Gideri, Yönetim Gideri Payı (%), TL, Sonrası Kar -->
              <div class="col-md-4">
                <label class="form-label">Yönetim Gideri</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(managementExpenseBeforeProfit)"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Yönetim Gideri Payı (%)</label>
                <input
                    type="number"
                    class="form-control"
                    :value="calculateManagementExpenseRate(expense)"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Yönetim Gideri Payı (TL)</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateManagementExpenseTL(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Yönetim Gideri Sonrası Kar</label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(calculateProfitAfterManagementExpense(expense))"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Yönetim Gideri Sonrası Kar Oranı (%)</label>
                <input
                    type="text"
                    class="form-control"
                    :value="calculateProfitAfterManagementRate(expense) + '%'"
                    readonly
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">
                  Diğer Giderler
                  <small class="text-muted d-block" style="font-size: 0.75rem; font-weight: normal;">
                    (Bu alan hiçbir hesaplamaya dahil değildir)
                  </small>
                </label>
                <input
                    type="text"
                    class="form-control"
                    :value="formatTL(expense.otherexpenses || 0)"
                    readonly
                />
              </div>

              <!-- Otomatik Kayıt Durumu -->
              <div class="d-flex justify-content-end gap-2">
                <div class="alert" :class="{
                  'alert-success': savedRecords[expense.personalid],
                  'alert-info': !savedRecords[expense.personalid]
                }">
                  <span v-if="savedRecords[expense.personalid]">
                    <i class="fas fa-check-circle me-1"></i> Otomatik kaydedildi
                  </span>
                  <span v-else>
                    <i class="fas fa-clock me-1"></i> Kayıt bekleniyor
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>


</template>



<script>

import apiClient from "@/axios";
import * as XLSX from "xlsx";

export default {
  props: {
    contractId: {
      type: [String, Number],
      required: false
    },
    contractItems: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  name: "ProjectProfitabilityComponent",
  components: {},
  data() {
    return {
      personalExpenses: [],
      seniorityCeiling: 0, // Assumed to be the MONTHLY salary cap for severance
      managementExpenseBeforeProfit: 0,
      managementExpenseRate: 0,
      totalOsgbSales: 0,
      isRecordSaved: false, // This seems unused, savedRecords is used instead
      savedRecords: {},
      personalIds: [], // This seems unused
      personalDurations: {}, // Stores { personalid: { daysfromstart: XXXX } }
      checkInterval: null, // Periyodik kontrol için interval
    };
  },
  watch: {
    contractItems: {
      handler(newVal, oldVal) {
        if (oldVal && newVal && oldVal.contractPrice !== newVal.contractPrice) {
          this.createAutomaticRecords();
        }
      },
      deep: true
    }
  },
  created() {
    this.getPersonalExpenses();
    this.getSeniorityCeiling();
    this.getManagementExpenseBeforeProfit();
    //this.getOsgbSalesTotal(); // This method seems to fetch from a different endpoint than fetchTotalOsgbSales
    this.savedRecords = {}; // Initialize
  },
  mounted() {
    this.fetchTotalOsgbSales();
    // The logic to fetch personalDurations is now primarily within getPersonalExpenses,
    // as it depends on personalExpenses being loaded first.
    setTimeout(() => {
      this.logAllCalculations();
    }, 3000);

    // Kayıt durumunu periyodik olarak kontrol et
    this.startPeriodicCheck();
  },

  methods: {
    async fetchTotalOsgbSales() {
      try {
        const response = await apiClient.get('/contract/total-valid-price');
        this.totalOsgbSales = response.data;
        console.log('Toplam OSGB satış tutarı alındı:', this.totalOsgbSales);
        await this.syncManagementExpenseRates();
      } catch (error) {
        console.error('Toplam OSGB satış tutarı alınırken hata oluştu:', error);
        this.totalOsgbSales = 0;
      }
    },
    checkSavedRecords() {
      if (this.contractId && this.personalExpenses.length > 0) {
        this.personalExpenses.forEach(expense => {
          if (expense.personalid) {
            apiClient.get(`/project-profitability-m/exists/${this.contractId}/${expense.personalid}`)
                .then(response => {
                  // Ensure reactivity when updating
                  this.savedRecords = { ...this.savedRecords, [expense.personalid]: response.data };
                })
                .catch(error => {
                  // Kontrol hatası
                  this.savedRecords = { ...this.savedRecords, [expense.personalid]: false };
                });
          }
        });
      }
    },

    calculateManagementExpenseRate(expense) {
      if (expense.managementExpenseRate !== undefined && expense.managementExpenseRate !== null) {
        const numericRate = Number(expense.managementExpenseRate);
        if (!isNaN(numericRate)) {
          return Number(numericRate.toFixed(4));
        }
      }

      const faturaTutari = Number(expense.invoiceamount || expense.contractprice || 0);
      const osgbSatisTutari = Number(this.totalOsgbSales || 0);

      if (!osgbSatisTutari) {
        return 0.0000;
      }

      const oran = faturaTutari / osgbSatisTutari;
      return Number(oran.toFixed(4));
    },
    // getOsgbSalesTotal seems unused if fetchTotalOsgbSales is primary
    /*
    getOsgbSalesTotal() {
      apiClient.get("/osgb-sales-amount")
          .then(response => {
            this.totalOsgbSales = response.data.reduce((total, sale) =>
                total + Number(sale.osgbSalesAmount), 0);
          })
          .catch(error => {
            console.error("OSGB satış verileri yüklenirken hata:", error);
          });
    },
    */
    calculateProfitBeforeManagement(expense) {
      const faturaTutari = Number(expense.invoiceamount || expense.contractprice || 0);
      const toplamMaliyet = this.calculateTotalCost(expense);
      return faturaTutari - toplamMaliyet;
    },
    calculateTotalCost(expense) {
      const brutUcret = this.calculateGrossSalary(expense);
      const sgkIsverenPayi = this.calculateSgkEmployerShare(expense);
      const kidemTutari = this.calculateSeniority(expense); // Uses the new calculation
      const giderlerToplami = this.calculateTotalExpenses(expense);
      return brutUcret + sgkIsverenPayi + kidemTutari + giderlerToplami;
    },
    calculateInsuranceBase(expense) {
      const brutUcret = this.calculateGrossSalary(expense);
      const sgkTavan = 195041.40; // This might need to be dynamic if it changes often
      return brutUcret >= sgkTavan ? sgkTavan : brutUcret;
    },
    calculateSgkEmployerShare(expense) {
      const sgkRate = Number(expense.sgkrate || 0);
      const insuranceBase = this.calculateInsuranceBase(expense);
      // SGK İşveren Payı = SGK Oranı / 100 × SGK Matrahı
      return (sgkRate / 100) * insuranceBase;
    },

    calculateSeniority(expense) {
      if (expense.seniorityburden) {
        return 0;
      }

      const brutUcret = this.calculateGrossSalary(expense);
      const kidemTavani = this.seniorityCeiling || 0;

      if (kidemTavani === 0 || brutUcret === 0) {
        return 0;
      }

      const kidemHesaplamaMatrahi = brutUcret <= kidemTavani ? brutUcret : kidemTavani;
      return kidemHesaplamaMatrahi / 12;
    },
    calculateProfitAfterManagementExpense(expense) {
      const faturaTutari = Number(expense.invoiceamount || expense.contractprice || 0);
      const toplamMaliyet = this.calculateTotalCost(expense);
      const yonetimGideriPayiTL = this.calculateManagementExpenseTL(expense);
      // Yönetim Gideri Sonrası Kar = Fatura Tutarı - Toplam Maliyet - Yönetim Gideri
      return faturaTutari - toplamMaliyet - yonetimGideriPayiTL;
    },
    calculateManagementExpenseTL(expense) {
      const managementRate = this.calculateManagementExpenseRate(expense);
      const managementExpenseValue = Number(this.managementExpenseBeforeProfit || 0);
      if (!managementExpenseValue) {
        return 0.00;
      }
      const yonetimGideriTL = managementRate * managementExpenseValue;
      return Number(yonetimGideriTL.toFixed(2));
    },
    calculateProfitBeforeManagementRate(expense) {
      // Yönetim Gideri Öncesi Kar Oranı = (Fatura Tutarı - Toplam Maliyet) / Toplam Maliyet * 100
      const faturaTutari = Number(expense.invoiceamount || expense.contractprice || 0);
      const toplamMaliyet = this.calculateTotalCost(expense);

      if (toplamMaliyet === 0) {
        return 0.00;
      }

      const yonetimGideriOncesiKar = faturaTutari - toplamMaliyet;
      const oran = (yonetimGideriOncesiKar / toplamMaliyet) * 100;
      return Number(oran.toFixed(2));
    },
    calculateProfitAfterManagementRate(expense) {
      // Yönetim Gideri Sonrası Kar Oranı = (Yönetim Gideri Sonrası Kar / Toplam Maliyet) * 100
      const toplamMaliyet = this.calculateTotalCost(expense);
      if (toplamMaliyet === 0) {
        return 0.00;
      }

      const yonetimGideriSonrasiKar = this.calculateProfitAfterManagementExpense(expense);
      const oran = (yonetimGideriSonrasiKar / toplamMaliyet) * 100;
      return Number(oran.toFixed(2));
    },
    getManagementExpenseBeforeProfit() {
      apiClient
          .get('companyManagementExpense')
          .then((response) => {
            // Assuming response.data is an array and has at least one element
            if (response.data && response.data.length > 0) {
              this.managementExpenseBeforeProfit = response.data[0].companyManagementExpense;
            } else {
              this.managementExpenseBeforeProfit = 0; // Default or error handling
            }
          })
          .catch((error) => {
            console.error("Yönetim gideri öncesi kar yüklenirken hata:", error);
            this.managementExpenseBeforeProfit = 0;
          });
    },
    calculateGrossSalary(expense) {
      const workedHours = Number(expense.workedhours || 0);
      const hourlyRate = Number(expense.hoursnetamount || 0); // If 'hoursnetamount' is net, this might not be "Gross Salary"
                                                              // but "Net Salary based on hours". Clarify if "Brüt Ücret" means "Gross" or "Net".
                                                              // Assuming 'hoursnetamount' leads to the intended "Brüt Ücret" for this context.
      return workedHours * hourlyRate;
    },
    calculateTotalExpenses(expense) {
      return (
          Number(expense.fuelexpenses || 0) +
          Number(expense.foodexpenses || 0) +
          Number(expense.travelexpenses || 0) +
          Number(expense.carrentexpenses || 0)
      );
    },
    async getPersonalExpenses() {
      if (this.contractId) {
        try {
          const response = await apiClient.get(`personal-expenses/contract/${this.contractId}`);
          const allExpenses = response.data;
          const filteredExpenses = [];
          const newPersonalDurations = { ...this.personalDurations };
          const newSavedRecords = { ...this.savedRecords };

          for (const expense of allExpenses) {
            if (expense.personalid) {
              try {
                const profitabilityResponse = await apiClient.get(`project-profitability-m/exists/${this.contractId}/${expense.personalid}`);

                if (profitabilityResponse.data === true) {
                  try {
                    const mongoRecordResponse = await apiClient.get(`project-profitability-m/active/${this.contractId}/${expense.personalid}`);
                    if (mongoRecordResponse.data) {
                      expense.projectProfitabilityId = mongoRecordResponse.data.id || mongoRecordResponse.data._id;
                      const managementRateFromMongo = Number(mongoRecordResponse.data.managementExpenseRate ?? 0);
                      expense.managementExpenseRate = isNaN(managementRateFromMongo) ? null : managementRateFromMongo;
                      expense.managementExpenseRateTL = mongoRecordResponse.data.managementExpenseRateTL;
                      expense.profitBeforeAdministrativeExpenses = mongoRecordResponse.data.profitBeforeAdministrativeExpenses;
                      expense.profitAfterAdministrativeExpenses = mongoRecordResponse.data.profitAfterAdministrativeExpenses;
                      expense.profitBeforeManagementRate = mongoRecordResponse.data.profitBeforeManagementRate;
                      expense.profitAfterManagementRate = mongoRecordResponse.data.profitAfterManagementRate;
                      expense.totalCost = mongoRecordResponse.data.totalCost;
                    }
                  } catch (mongoError) {
                    console.warn(`MongoDB kaydı alınamadı: ${expense.personalid}`, mongoError);
                  }

                  filteredExpenses.push(expense);

                  if (newSavedRecords[expense.personalid] === undefined) {
                    newSavedRecords[expense.personalid] = false;
                  }

                  apiClient.get(`personal/${expense.personalid}/duration`)
                      .then(durationResponse => {
                        newPersonalDurations[expense.personalid] = durationResponse.data;
                        this.personalDurations = { ...newPersonalDurations };
                      })
                      .catch(error => {
                        console.error(`Kişi süresi alınamadı: ${expense.personalid}`, error);
                        newPersonalDurations[expense.personalid] = { daysfromstart: 0 };
                        this.personalDurations = { ...newPersonalDurations };
                      });
                }
              } catch (error) {
                console.warn(`Proje karlılık kaydı kontrol edilemedi: ${expense.personalid}`, error);
              }
            }
          }

          this.personalExpenses = filteredExpenses;
          this.savedRecords = newSavedRecords;
          this.checkSavedRecords();
          await this.syncManagementExpenseRates();

          console.log(`Proje karlılık için ${filteredExpenses.length} personel bulundu (toplam ${allExpenses.length} personel arasından)`);
        } catch (error) {
          console.error("Veri yükleme hatası:", error);
        }
      }
    },
    getSeniorityCeiling() {
      apiClient
          .get('seniorityCeiling')
          .then((response) => {
            if (response.data && response.data.length > 0) {
              this.seniorityCeiling = response.data[0].seniorityCeilingPrice;
              console.log('Kıdem tavanı güncellendi:', this.seniorityCeiling);
            } else {
              this.seniorityCeiling = 0; // Default or error handling
            }
          })
          .catch((error) => {
            console.error("Kıdem tavan ücreti yüklenirken hata:", error);
            this.seniorityCeiling = 0; // Set to a safe default on error
          });
    },


    // Bildirim gösterme fonksiyonu
    showNotification(message, type = "info") {
      // Bootstrap alert kullanarak bildirim göster
      const alertDiv = document.createElement('div');
      alertDiv.className = `alert alert-${type === 'error' ? 'danger' : type} alert-dismissible fade show position-fixed`;
      alertDiv.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alertDiv.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      `;

      document.body.appendChild(alertDiv);

      // 3 saniye sonra otomatik kapat
      setTimeout(() => {
        if (alertDiv.parentNode) {
          alertDiv.parentNode.removeChild(alertDiv);
        }
      }, 3000);
    },
    formatTL(value) {
      if (isNaN(parseFloat(value))) { // Handle cases where value might not be a number
        return new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: 'TRY',
          minimumFractionDigits: 2
        }).format(0);
      }
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2
      }).format(value);
    },
    formatSgkRate(rate) {
      const numeric = Number(rate);
      if (isNaN(numeric)) {
        return '%0.00';
      }
      return `%${numeric.toFixed(2)}`;
    },
    logAllCalculations() {
      console.log('=== FRONTEND HESAPLAMALAR ===');
      this.personalExpenses.forEach(expense => {
        const calculations = {
          personalName: expense.personalname,
          personalId: expense.personalid,
          contractId: this.contractId,
          rawData: {
            workedHours: expense.workedhours,
            hourlyRate: expense.hoursnetamount,
            contractPrice: expense.contractprice,
            sgkRate: expense.sgkrate,
            insuranceDay: expense.insuranceday,
            expenses: {
              fuel: expense.fuelexpenses,
              food: expense.foodexpenses,
              travel: expense.travelexpenses,
              carRent: expense.carrentexpenses,
              other: expense.otherexpenses
            }
          },
          calculations: {
            monthGrossAmount: this.calculateGrossSalary(expense),
            insuranceBase: this.calculateInsuranceBase(expense),
            sgkEmployerShare: this.calculateSgkEmployerShare(expense),
            severanceAmount: this.calculateSeniority(expense),
            totalExpenses: this.calculateTotalExpenses(expense),
            totalCost: this.calculateTotalCost(expense),
            profitBeforeAdmin: this.calculateProfitBeforeManagement(expense),
            managementExpenseRate: this.calculateManagementExpenseRate(expense),
            managementExpenseTL: this.calculateManagementExpenseTL(expense),
            profitAfterAdmin: this.calculateProfitAfterManagementExpense(expense),
            profitBeforeManagementRate: this.calculateProfitBeforeManagementRate(expense),
            profitAfterManagementRate: this.calculateProfitAfterManagementRate(expense)
          }
        };
        console.log(`FRONTEND - ${expense.personalname}:`, calculations);
      });
    },
    startPeriodicCheck() {
      // Her 2 saniyede bir kayıt durumunu kontrol et
      this.checkInterval = setInterval(() => {
        if (this.contractId && this.personalExpenses.length > 0) {
          this.checkSavedRecords();
        }
      }, 2000);
    },
    async syncManagementExpenseRates() {
      if (!this.contractId || !this.personalExpenses.length || !this.totalOsgbSales) {
        return;
      }

      for (const expense of this.personalExpenses) {
        const calculatedRate = this.calculateManagementExpenseRate({ ...expense, managementExpenseRate: null });
        const backendRate = Number(expense.managementExpenseRate ?? 0);

        if (Math.abs(calculatedRate - backendRate) < 0.0001) {
          continue;
        }

        await this.persistManagementExpenseRate(expense);
      }
    },
    async persistManagementExpenseRate(expense) {
      if (!expense.projectProfitabilityId || !expense.personalid) {
        return;
      }

      try {
        const response = await apiClient.put(
            `/project-profitability-m/update/${expense.projectProfitabilityId}`,
            {
              contractId: this.contractId,
              personalId: expense.personalid
            }
        );

        if (response?.data) {
          const updatedRate = Number(response.data.managementExpenseRate ?? 0);
          expense.managementExpenseRate = isNaN(updatedRate)
              ? this.calculateManagementExpenseRate(expense)
              : Number(updatedRate.toFixed(4));
          expense.managementExpenseRateTL = response.data.managementExpenseRateTL ?? expense.managementExpenseRateTL;
        }

        this.savedRecords = { ...this.savedRecords, [expense.personalid]: true };
      } catch (error) {
        console.error(`Yönetim gideri payı senkronizasyonu sırasında hata: ${expense.personalname}`, error);
        this.showNotification(`Yönetim gideri payı güncellenemedi (${expense.personalname})`, 'error');
      }
    },
    exportToExcel() {
      if (!this.personalExpenses || this.personalExpenses.length === 0) {
        this.showNotification("Dışa aktarılacak veri bulunmamaktadır.", "warning");
        return;
      }

      // Excel için veri hazırlama
      const excelData = this.personalExpenses.map((expense, index) => {
        const grossSalary = this.calculateGrossSalary(expense);
        const insuranceBase = this.calculateInsuranceBase(expense);
        const sgkEmployerShare = this.calculateSgkEmployerShare(expense);
        const seniority = this.calculateSeniority(expense);
        const totalExpenses = this.calculateTotalExpenses(expense);
        const totalCost = this.calculateTotalCost(expense);
        const invoiceAmount = Number(expense.invoiceamount || expense.contractprice || 0);
        const profitBeforeManagement = this.calculateProfitBeforeManagement(expense);
        const profitBeforeManagementRate = this.calculateProfitBeforeManagementRate(expense);
        const managementExpenseRate = this.calculateManagementExpenseRate(expense);
        const managementExpenseTL = this.calculateManagementExpenseTL(expense);
        const profitAfterManagement = this.calculateProfitAfterManagementExpense(expense);
        const profitAfterManagementRate = this.calculateProfitAfterManagementRate(expense);

        return {
          "No": index + 1,
          "Personel Adı": expense.personalname || "",
          "Personel Rolü": expense.personalrolename || "",
          "OSGB Satış Tutarı": this.formatTL(this.totalOsgbSales),
          "Brüt Ücret": this.formatTL(grossSalary),
          "SGK Matrahı": this.formatTL(insuranceBase),
          "SGK Günü": expense.insuranceday || 0,
          "SGK Oranı (%)": this.formatSgkRate(expense.sgkrate),
          "SGK İşveren Payı": this.formatTL(sgkEmployerShare),
          "Kıdem Tutarı": this.formatTL(seniority),
          "Giderler Toplamı": this.formatTL(totalExpenses),
          "Toplam Maliyet": this.formatTL(totalCost),
          "Fatura Tutarı (Personel)": this.formatTL(invoiceAmount),
          "Yönetim Gideri Öncesi Kar": this.formatTL(profitBeforeManagement),
          "Yönetim Gideri Öncesi Kar Oranı (%)": profitBeforeManagementRate + '%',
          "Yönetim Gideri": this.formatTL(this.managementExpenseBeforeProfit),
          "Yönetim Gideri Payı (%)": (managementExpenseRate * 100).toFixed(4) + '%',
          "Yönetim Gideri Payı (TL)": this.formatTL(managementExpenseTL),
          "Yönetim Gideri Sonrası Kar": this.formatTL(profitAfterManagement),
          "Yönetim Gideri Sonrası Kar Oranı (%)": profitAfterManagementRate + '%',
          "Diğer Giderler": this.formatTL(expense.otherexpenses || 0)
        };
      });

      // Toplam satırı ekle
      const totalsRow = {
        "No": "TOPLAM",
        "Personel Adı": "",
        "Personel Rolü": "",
        "OSGB Satış Tutarı": this.formatTL(this.totalOsgbSales),
        "Brüt Ücret": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateGrossSalary(exp), 0)
        ),
        "SGK Matrahı": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateInsuranceBase(exp), 0)
        ),
        "SGK Günü": "",
        "SGK Oranı (%)": "",
        "SGK İşveren Payı": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateSgkEmployerShare(exp), 0)
        ),
        "Kıdem Tutarı": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateSeniority(exp), 0)
        ),
        "Giderler Toplamı": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateTotalExpenses(exp), 0)
        ),
        "Toplam Maliyet": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateTotalCost(exp), 0)
        ),
        "Fatura Tutarı (Personel)": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + Number(exp.invoiceamount || exp.contractprice || 0), 0)
        ),
        "Yönetim Gideri Öncesi Kar": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateProfitBeforeManagement(exp), 0)
        ),
        "Yönetim Gideri Öncesi Kar Oranı (%)": "",
        "Yönetim Gideri": this.formatTL(this.managementExpenseBeforeProfit),
        "Yönetim Gideri Payı (%)": "",
        "Yönetim Gideri Payı (TL)": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateManagementExpenseTL(exp), 0)
        ),
        "Yönetim Gideri Sonrası Kar": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + this.calculateProfitAfterManagementExpense(exp), 0)
        ),
        "Yönetim Gideri Sonrası Kar Oranı (%)": "",
        "Diğer Giderler": this.formatTL(
          this.personalExpenses.reduce((sum, exp) => sum + Number(exp.otherexpenses || 0), 0)
        )
      };

      excelData.push({}); // Boş satır
      excelData.push(totalsRow);

      // Excel dosyası oluştur
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      
      // Sütun genişliklerini ayarla
      const columnWidths = [
        { wch: 5 },   // No
        { wch: 25 },  // Personel Adı
        { wch: 20 },  // Personel Rolü
        { wch: 18 },  // OSGB Satış Tutarı
        { wch: 15 },  // Brüt Ücret
        { wch: 15 },  // SGK Matrahı
        { wch: 12 },  // SGK Günü
        { wch: 15 },  // SGK Oranı
        { wch: 18 },  // SGK İşveren Payı
        { wch: 15 },  // Kıdem Tutarı
        { wch: 18 },  // Giderler Toplamı
        { wch: 18 },  // Toplam Maliyet
        { wch: 22 },  // Fatura Tutarı
        { wch: 25 },  // Yönetim Gideri Öncesi Kar
        { wch: 30 },  // Yönetim Gideri Öncesi Kar Oranı
        { wch: 18 },  // Yönetim Gideri
        { wch: 22 },  // Yönetim Gideri Payı (%)
        { wch: 22 },  // Yönetim Gideri Payı (TL)
        { wch: 28 },  // Yönetim Gideri Sonrası Kar
        { wch: 33 },  // Yönetim Gideri Sonrası Kar Oranı
        { wch: 18 }   // Diğer Giderler
      ];
      worksheet['!cols'] = columnWidths;

      const contractName = this.contractItems?.contractName || "sozlesme";
      const sheetName = "Proje Karlılık";
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      
      const fileName = `proje_karlilik_${contractName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      this.showNotification("Excel dosyası başarıyla oluşturuldu.", "success");
    }
  },
  beforeUnmount() {
    // Interval'ı temizle
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }
};
</script>
